import { createBrowserRouter, Outlet, Navigate } from "react-router-dom";
import { ApplicationsPage } from "./features/applications/applications-page";
import { CreateApplicationPage } from "./features/applications/create-application-page";
import { ManageApplicationPage } from "./features/applications/manage-application-page";
import { Dashboard } from "./features/dashboard/dashboard";
import ForbiddenPage from "./features/errors/forbidden";
import AuthErrorPage from "./features/errors/auth-error";
import { CreateGroupPage } from "./features/groups/create-group-page";
import { GroupsPage } from "./features/groups/groups-page";
import { ManageGroupPage } from "./features/groups/manage-group-page";
import { CreateIdpPage } from "./features/idps/create-idp-page";
import { IdpsPage } from "./features/idps/idps-page";
import { ManageIdpPage } from "./features/idps/manage-idp-page";
import { CreatePermissionPage } from "./features/permissions/create-permission-page";
import { ManagePermissionPage } from "./features/permissions/manage-permission-page";
import { PermissionsPage } from "./features/permissions/permissions-page";
import { ResourcesPage } from "./features/resources/resources-page";
import LoginPage from "./features/platform/pages/login";
import WaitingRoomPage from "./features/platform/pages/waiting-room";
import { CreateRolePage } from "./features/roles/create-role-page";
import { ManageRolePage } from "./features/roles/manage-role-page";
import { RolesPage } from "./features/roles/roles-page";
import { CreateTenantPage } from "./features/tenants/create-tenant-page";
import { ManageTenantPage } from "./features/tenants/manage-tenant-page";
import { AddTenantIdpPage } from "./features/tenants/add-tenant-idp-page";
import { ConfigureTenantIdpPage } from "./features/tenants/configure-tenant-idp-page";
import { TenantsPage } from "./features/tenants/tenants-page";
import { CreateUserPage } from "./features/users/create-user-page";
import { ManageUserPage } from "./features/users/manage-user-page";
import { UsersPage } from "./features/users/users-page";
import { ServicePrincipalsPage } from "./features/service-principals/service-principals-page";
import { CreateServicePrincipalPage } from "./features/service-principals/create-service-principal-page";
import { ManageServicePrincipalPage } from "./features/service-principals/manage-service-principal-page";
import { ManagementLayout } from "./layouts/mgmt";
import { AuthProvider } from "./features/platform/contexts/auth-context";
import { AssignmentsPage } from "./features/assignments/assignments-page";
import { CreateAssignmentPage } from "./features/assignments/create-assignment-page";
import { ManageAssignmentPage } from "./features/assignments/manage-assignment-page";
import { PortalPage } from "./features/portal/portal-page";
import { PortalLayout } from "./features/portal/portal-layout";
import { TasksPage } from "./features/tasks/tasks-page";
import { UserAccessApprovalPage } from "./features/tasks/user-access-approval-page";
import { ActivitiesPage } from "./features/activities/activities-page";
import { AnalyticsPage } from "./features/analytics/analytics-page";
import { EventsPage } from "./features/events/events-page";
import { TokensPage } from "./features/tokens/tokens-page";
import { PreferencesPage } from "./features/preferences/preferences-page";
import { ConfigurationsPage } from "./features/configurations/configurations-page";
import { CreateEmailServicePage } from "./features/configurations/create-email-service-page";
import { ManageEmailServicePage } from "./features/configurations/manage-email-service-page";
import { MailPage } from "./features/mail/mail-page";
import { ManageMailTemplatePage } from "./features/mail/manage-mail-template-page";
import { DocsLayout, FaqPage, IntegrationsPage, OktaOinGuidePage } from "./features/docs";
import { PlatformDocsLayout, PlatformDocsPage } from "./features/dev-docs";



import { ErrorPage } from "./features/errors/error-page";

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "auth/login",
        element: <LoginPage />,
      },
      {
        path: "auth/error",
        element: <AuthErrorPage />,
      },
      {
        path: "t",
        children: [
          {
            path: "",
            element: <LoginPage />,
          },
          {
            path: ":slug",
            element: (
              <PortalLayout>
                <Outlet />
              </PortalLayout>
            ),
            children: [
              {
                index: true,
                element: <PortalPage />,
              },
              {
                path: "waiting-room",
                element: <WaitingRoomPage />,
              },
            ],
          },
          {
            path: "error",
            children: [
              {
                path: "forbidden",
                element: <ForbiddenPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/m",
    element: (
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        element: <ManagementLayout />,
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: "tenants",
            children: [
              {
                index: true,
                element: <TenantsPage />,
              },
              {
                path: "create",
                element: <CreateTenantPage />,
              },
              {
                path: ":id",
                element: <ManageTenantPage />,
              },
              {
                path: ":id/idps/add",
                element: <AddTenantIdpPage />,
              },
              {
                path: ":id/idps/:idpId",
                element: <ConfigureTenantIdpPage />,
              },
            ],
          },
          {
            path: "users",
            children: [
              {
                index: true,
                element: <UsersPage />,
              },
              {
                path: "create",
                element: <CreateUserPage />,
              },
              {
                path: ":id",
                element: <ManageUserPage />,
              },
            ],
          },
          {
            path: "groups",
            children: [
              {
                index: true,
                element: <GroupsPage />,
              },
              {
                path: "create",
                element: <CreateGroupPage />,
              },
              {
                path: ":id",
                element: <ManageGroupPage />,
              },
            ],
          },
          {
            path: "applications",
            children: [
              {
                index: true,
                element: <ApplicationsPage />,
              },
              {
                path: "create",
                element: <CreateApplicationPage />,
              },
              {
                path: ":id",
                element: <ManageApplicationPage />,
              },
            ],
          },
          {
            path: "resources",
            children: [
              {
                index: true,
                element: <ResourcesPage />,
              },
            ],
          },
          {
            path: "idps",
            children: [
              {
                index: true,
                element: <IdpsPage />,
              },
              {
                path: "create",
                element: <CreateIdpPage />,
              },
              {
                path: ":id",
                element: <ManageIdpPage />,
              },
            ],
          },
          {
            path: "service-principals",
            children: [
              {
                index: true,
                element: <ServicePrincipalsPage />,
              },
              {
                path: "create",
                element: <CreateServicePrincipalPage />,
              },
              {
                path: ":id",
                element: <ManageServicePrincipalPage />,
              },
            ],
          },
          {
            path: "roles",
            children: [
              {
                index: true,
                element: <RolesPage />,
              },
              {
                path: "create",
                element: <CreateRolePage />,
              },
              {
                path: ":id",
                element: <ManageRolePage />,
              },
            ],
          },
          {
            path: "permissions",
            children: [
              {
                index: true,
                element: <PermissionsPage />,
              },
              {
                path: "create",
                element: <CreatePermissionPage />,
              },
              {
                path: ":id",
                element: <ManagePermissionPage />,
              },
            ],
          },
          {
            path: "assignments",
            children: [
              {
                index: true,
                element: <AssignmentsPage />,
              },
              {
                path: "create",
                element: <CreateAssignmentPage />,
              },
              {
                path: ":id",
                element: <ManageAssignmentPage />,
              }
            ],
          },
          {
            path: "tasks",
            children: [
              {
                index: true,
                element: <TasksPage />,
              },
              {
                path: "approvals/user-access/:id",
                element: <UserAccessApprovalPage />,
              }
            ],
          },
          {
            path: "activities",
            children: [
              {
                index: true,
                element: <ActivitiesPage />,
              }
            ],
          },
          {
            path: "analytics",
            element: <AnalyticsPage />,
          },
          {
            path: "events",
            element: <EventsPage />,
          },
          {
            path: "tokens",
            element: <TokensPage />,
          },
          {
            path: "preferences",
            element: <PreferencesPage />,
          },
          {
            path: "mail",
            children: [
              {
                index: true,
                element: <MailPage />,
              },
              {
                path: ":id",
                element: <ManageMailTemplatePage />,
              },
            ],
          },
          {
            path: "configurations",
            children: [
              {
                index: true,
                element: <ConfigurationsPage />,
              },
              {
                path: ":tab",
                children: [
                  {
                    index: true,
                    element: <ConfigurationsPage />,
                  },
                  {
                    path: "create",
                    element: <CreateEmailServicePage />,
                  },
                  {
                    path: ":id",
                    element: <ManageEmailServicePage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/dev/docs/platform",
    element: (
      <AuthProvider>
        <PlatformDocsLayout />
      </AuthProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <PlatformDocsPage />,
      },
      {
        path: ":pageId",
        element: <PlatformDocsPage />,
      },
    ],
  },
  {
    path: "/docs",
    element: <DocsLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/docs/faq" replace />,
      },
      {
        path: "faq",
        element: <FaqPage />,
      },
      {
        path: "okta-oin-guide",
        element: <OktaOinGuidePage />,
      },
      {
        path: "integrations",
        children: [
          {
            index: true,
            element: <IntegrationsPage />,
          },
          {
            path: "okta/oin",
            element: <OktaOinGuidePage />,
          },
        ],
      },
      {
        path: "*",
        element: <ErrorPage status={404} />,
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage status={404} />,
  },
]);

